// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadOrderApprovals from "@insite/client-framework/Store/Data/OrderApprovals/Handlers/LoadOrderApprovals";
import approveOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/ApproveOrder";
import deleteOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/DeleteOrder";
import editOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/EditOrder";
import requestRevision from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/RequestRevision";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import { HiddenProps } from "@insite/mobius/Hidden";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xDeclineOrder from "@vanmeter/App/Handlers/OrderApprovalDetails/XngDeclineOrder";
import xLoadingOverlay from "@vanmeter/App/Handlers/XNG/LoadingOverlay";
import Link, { LinkPresentationProps } from "@vanmeter/MobiusOverrides/Link/Link";
import TextArea, { TextAreaPresentationProps } from "@vanmeter/MobiusOverrides/TextArea/TextArea";
import React, { useState } from "react";

import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import siteMessage from "@insite/client-framework/SiteMessage";

interface OwnProps {
    cart: CartModel;
    extendedStyles?: OrderApprovalDetailsActionButtonsStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    account: getCurrentAccountState(state)?.value,
    cartPageLink: getPageLinkByPageType(state, "CartPage"),
    orderApprovalListPageLink: getPageLinkByPageType(state, "OrderApprovalListPage"),
    homeLink: getPageLinkByPageType(state, "HomePage"),
    // XNG-Changes: refresh list after decline
    getOrderApprovalsParameter: state.pages.orderApprovalList.getOrderApprovalsParameter,
});

const mapDispatchToProps = {
    approveOrder,
    deleteOrder,
    editOrder,
    requestRevision,
    // XNG-Changes: add loading spinner on delete and approve order
    xLoadingOverlay,
    xDeclineOrder,
    loadOrderApprovals,
};

export interface OrderApprovalDetailsActionButtonsStyles {
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    printButton?: ButtonPresentationProps;
    showActivityLogButton?: ButtonPresentationProps;
    continueShoppingButton?: ButtonPresentationProps;
    deleteButton?: ButtonPresentationProps;
    editButton?: ButtonPresentationProps;
    requestRevisionButton?: ButtonPresentationProps;
    approveButton?: ButtonPresentationProps;
    printClickable?: ClickableProps;
    showActivityLogClickable?: ClickableProps;
    continueShoppingClickable?: ClickableProps;
    deleteClickable?: ClickableProps;
    editClickable?: ClickableProps;
    requestRevisionClickable?: ClickableProps;
    approveClickable?: ClickableProps;
    twoButtonModalStyles?: TwoButtonModalStyles;
    requestRevisionModal?: ModalPresentationProps;
    requestRevisionContainer?: GridContainerProps;
    requestRevisionMessageGridItem?: GridItemProps;
    requestRevisionMessageText?: TypographyPresentationProps;
    requestRevisionMessageTextArea?: TextAreaPresentationProps;
    requestRevisionButtonsGridItem?: GridItemProps;
    requestRevisionSendRequestButton?: ButtonPresentationProps;
    requestRevisionCancelButton?: ButtonPresentationProps;
    // XNG-Changes
    actionsWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    modalWrapper?: InjectableCss;
    modal?: ModalPresentationProps;
    deleteLink?: LinkPresentationProps;
    requestRevisionLink?: LinkPresentationProps;
    approveLink?: LinkPresentationProps;
    editLink?: LinkPresentationProps;
    continueShoppingLink?: LinkPresentationProps;
    printLink?: LinkPresentationProps;
    cancelLink?: LinkPresentationProps;
    declineLink?: LinkPresentationProps;
    sendRequestLink?: LinkPresentationProps;
}

export const orderApprovalDetailsActionButtonsStyles: OrderApprovalDetailsActionButtonsStyles = {
    deleteButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    showActivityLogButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    editButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    requestRevisionButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    continueShoppingButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    approveButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    printButton: {
        variant: "secondary",
        buttonType: "solid",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
    requestRevisionModal: {
        size: 600,
        cssOverrides: {
            modalContent: css`
                padding: 15px 30px 20px 30px;
            `,
        },
    },
    requestRevisionContainer: {
        gap: 10,
    },
    requestRevisionMessageGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    requestRevisionMessageText: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    requestRevisionButtonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    requestRevisionCancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

const OrderApprovalDetailsActionButtons = ({
    cart,
    history,
    cartPageLink,
    orderApprovalListPageLink,
    homeLink,
    approveOrder,
    deleteOrder,
    editOrder,
    requestRevision,
    extendedStyles,
    account,
    toaster,
    // XNG-Changes
    xLoadingOverlay,
    xDeclineOrder,
    getOrderApprovalsParameter,
    loadOrderApprovals,
}: Props) => {
    const [styles] = useState(() => mergeToNew(orderApprovalDetailsActionButtonsStyles, extendedStyles));
    const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
    const [requestRevisionModalIsOpen, setRequestRevisionModalIsOpen] = useState(false);
    const [requestRevisionMessage, setRequestRevisionMessage] = useState("");
    const [isDisabledApproveButton, setIsDisabledApproveButton] = useState(false);
    // XNG-Changes
    const [declineReason, setDeclineReason] = useState("");
    const [declineModalIsOpen, setDeclineModalIsOpen] = useState(false);

    if (!cart || !account || !orderApprovalListPageLink || !cartPageLink) {
        return null;
    }

    const isBuyerEditingOrder = cart.orderApprovalStatus === "EditingInProcess";
    const isRevisionRequested = cart.orderApprovalStatus === "RevisionRequested";
    const isApprovalInProcess = cart.orderApprovalStatus === "ApprovalInProcess";

    const approvalDisabled =
        cart.cartLines?.every(cartline => cartline.isRestricted) || isDisabledApproveButton || isBuyerEditingOrder;

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    const editClickHandler = () => {
        // XNG-Change
        xLoadingOverlay({ key: "add", id: "edit_order" });
        editOrder({
            cart,
            onSuccess: () => {
                history.push(cartPageLink.url);
            },
            onComplete: () => {
                // XNG-Change
                xLoadingOverlay({ key: "clear", id: "edit_order" });
            },
        });
    };

    // XNG-Changes
    // const continueShoppingClickHandler = () => {
    //     if (homeLink) {
    //         history.push(homeLink.url);
    //     }
    // };

    // XNG-Changes
    const declineMessageHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDeclineReason(event.target.value);
    };

    // XNG-Changes

    const deleteClickHandler = () => {
        xLoadingOverlay({ key: "add", id: "delete_order_approval" });
        // XNG-Changes
        if (account.canApproveOrders) {
            xDeclineOrder({
                OrderNumber: cart.orderNumber,
                Reason: declineReason,

                onSuccess: () => {
                    toaster.addToast({
                        body: translate("Order #{0} Deleted", cart.orderNumber),
                        messageType: "success",
                    });
                    loadOrderApprovals(getOrderApprovalsParameter);
                    history.push(orderApprovalListPageLink.url);
                    xLoadingOverlay({ key: "clear", id: "delete_order_approval" });
                },
                onError: error => {
                    toaster.addToast({ body: error, messageType: "danger" });
                    xLoadingOverlay({ key: "clear", id: "delete_order_approval" });
                },
            });
            return;
        }
        deleteOrder({
            onSuccess: () => {
                toaster.addToast({ body: translate("Order #{0} Deleted", cart.orderNumber), messageType: "success" });
                history.push(orderApprovalListPageLink.url);
            },
            // XNG-Changes
            onComplete: () => {
                xLoadingOverlay({ key: "clear", id: "delete_order_approval" });
            },
        });
    };

    const requestRevisionClickHandler = () => {
        setRequestRevisionModalIsOpen(true);
    };

    const requestRevisionModalCloseHandler = () => {
        setRequestRevisionModalIsOpen(false);
    };

    const approveClickHandler = () => {
        setIsDisabledApproveButton(true);
        // XNG-Changes
        xLoadingOverlay({ key: "add", id: "approve_order" });
        approveOrder({
            onSuccess: () => {
                history.push(cartPageLink.url);
                // XNG-Changes
                xLoadingOverlay({ key: "clear", id: "approve_order" });
            },
            onComplete() {
                setTimeout(() => {
                    setIsDisabledApproveButton(false);
                }, 500);
            },
        });
    };

    const requestRevisionMessageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setRequestRevisionMessage(event.target.value);
    };

    const sendRequestButtonClickHandler = () => {
        requestRevision({
            cart,
            requestRevisionMessage,
            onSuccess: () => {
                toaster.addToast({ body: translate("Request sent"), messageType: "success" });
            },
        });
    };

    const printLabel = translate("Print");
    const deleteLabel = translate("Delete");
    const editLabel = translate("Edit");
    const requestRevisionLabel = translate("Request Revision");
    const approveLabel = translate("Approve");
    // XNG-Changes
    // const continueShoppingLabel = translate("Continue Shopping");

    return (
        <>
            {/* <Hidden {...styles.menuHiddenContainer}>
                <OverflowMenu position="end" {...styles.narrowOverflowMenu}>
                    <Clickable {...styles.printClickable} onClick={printOrOpenPrintAllModal}>
                        {printLabel}
                    </Clickable>
                    <Clickable
                        {...styles.deleteClickable}
                        disabled={isBuyerEditingOrder || isApprovalInProcess}
                        onClick={() => setConfirmationModalIsOpen(true)}
                    >
                        {deleteLabel}
                    </Clickable>
                    {account.canApproveOrders ? (
                        <>
                            <Clickable
                                disabled={isBuyerEditingOrder || isRevisionRequested || isApprovalInProcess}
                                {...styles.requestRevisionClickable}
                                onClick={requestRevisionClickHandler}
                            >
                                {requestRevisionLabel}
                            </Clickable>
                            <Clickable
                                disabled={approvalDisabled}
                                {...styles.approveClickable}
                                onClick={approveClickHandler}
                            >
                                {approveLabel}
                            </Clickable>
                        </>
                    ) : (
                        <>
                            <Clickable
                                {...styles.editClickable}
                                disabled={isApprovalInProcess}
                                onClick={editClickHandler}
                            >
                                {editLabel}
                            </Clickable>
                            <Clickable {...styles.continueShoppingClickable} onClick={continueShoppingClickHandler}>
                                {continueShoppingLabel}
                            </Clickable>
                        </>
                    )}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.buttonsHiddenContainer}> */}
            <Link
                data-test-selector="orderApprovalDetails_print"
                {...styles.printLink}
                onClick={printOrOpenPrintAllModal}
                // XNG-Changes
                aria-label={printLabel}
            >
                {printLabel}
            </Link>
            {/* XNG-Changes: approvers use Decline Order instead of Delete */}
            {!account.canApproveOrders && (
                <Link
                    data-test-selector="orderApprovalDetails_deleteOrder"
                    {...styles.deleteLink}
                    disabled={isBuyerEditingOrder || isApprovalInProcess}
                    onClick={() => setConfirmationModalIsOpen(true)}
                    aria-label={deleteLabel}
                >
                    {deleteLabel}
                </Link>
            )}
            {account.canApproveOrders ? (
                <>
                    {/* XNG-Changes */}
                    <Link
                        data-test-selector="orderApprovalDetails_declineOrder"
                        {...styles.deleteLink}
                        disabled={isBuyerEditingOrder || isApprovalInProcess}
                        onClick={() => setDeclineModalIsOpen(true)}
                        aria-label={translate("Decline Order")}
                    >
                        {translate("Decline Order")}
                    </Link>
                    <Link
                        data-test-selector="orderApprovalDetails_requestRevision"
                        disabled={isBuyerEditingOrder || isRevisionRequested || isApprovalInProcess}
                        {...styles.requestRevisionLink}
                        onClick={requestRevisionClickHandler}
                        // XNG-Changes
                        aria-label={requestRevisionLabel}
                    >
                        {requestRevisionLabel}
                    </Link>
                    <Link
                        data-test-selector="orderApprovalDetails_approveOrder"
                        {...styles.approveLink}
                        disabled={approvalDisabled}
                        onClick={approveClickHandler}
                        // XNG-Changes
                        aria-label={approveLabel}
                    >
                        {approveLabel}
                    </Link>
                </>
            ) : (
                <>
                    <Link
                        data-test-selector="orderApprovalDetails_editOrder"
                        {...styles.editLink}
                        disabled={isApprovalInProcess}
                        onClick={editClickHandler}
                        // XNG-Changes
                        aria-label={editLabel}
                    >
                        {editLabel}
                    </Link>
                    {/* <Button
                        data-test-selector="orderApprovalDetails_continueShopping"
                        {...styles.continueShoppingButton}
                        onClick={continueShoppingClickHandler}
                    >
                        {continueShoppingLabel}
                    </Button> */}
                </>
            )}
            {/* </Hidden> */}
            <TwoButtonModal
                modalIsOpen={confirmationModalIsOpen}
                headlineText={translate("Delete Order")}
                messageText={translate("Do you want to delete order #{0}?", cart.orderNumber)}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                submitTestSelector="confirmDeleteOrderButton"
                extendedStyles={styles.twoButtonModalStyles}
                onCancel={() => setConfirmationModalIsOpen(false)}
                onSubmit={deleteClickHandler}
            ></TwoButtonModal>
            <Modal
                {...styles.requestRevisionModal}
                headline={translate("Request Revision")}
                isOpen={requestRevisionModalIsOpen}
                handleClose={requestRevisionModalCloseHandler}
            >
                <GridContainer {...styles.requestRevisionContainer}>
                    <GridItem {...styles.requestRevisionMessageGridItem}>
                        <Typography {...styles.requestRevisionMessageText}>
                            {siteMessage("OrderApproval_RequestRevisionMessage")}
                        </Typography>
                        <TextArea
                            label={translate("Add a note (optional)")}
                            placeholder={translate("Write a message...")}
                            value={requestRevisionMessage}
                            onChange={requestRevisionMessageChangeHandler}
                            data-test-selector="requestRevisionMessage"
                            {...styles.requestRevisionMessageTextArea}
                        />
                    </GridItem>
                    <GridItem {...styles.requestRevisionButtonsGridItem}>
                        <Button
                            {...styles.requestRevisionCancelButton}
                            onClick={requestRevisionModalCloseHandler}
                            data-test-selector="requestRevisionModalCancel"
                            // XNG-Changes
                            aria-label={translate("Cancel")}
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.requestRevisionSendRequestButton}
                            onClick={sendRequestButtonClickHandler}
                            data-test-selector="requestRevisionModalSend"
                            // XNG-Changes
                            aria-label={translate("Send request")}
                        >
                            {translate("Send request")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Modal>
            {/* XNG-Changes */}
            <Modal
                {...styles.modal}
                isOpen={declineModalIsOpen}
                headline={translate("Decline Order")}
                isCloseable
                handleClose={() => setDeclineModalIsOpen(false)}
                sizeVariant="small"
            >
                <StyledWrapper {...styles.modalWrapper}>
                    <TextArea
                        label={translate("Decline Message")}
                        value={declineReason}
                        onChange={declineMessageHandler}
                        data-test-selector="declineOrderMessage"
                        rows={4}
                        required
                        maxLength={255}
                    />
                    <StyledWrapper {...styles.actionsWrapper}>
                        <Button
                            {...styles.cancelButton}
                            onClick={() => setDeclineModalIsOpen(false)}
                            data-test-selector="orderApprovalDetails_cancel"
                            // XNG-Changes
                            aria-label={translate("Cancel")}
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.submitButton}
                            onClick={deleteClickHandler}
                            data-test-selector="orderApprovalDetails_decline"
                            // XNG-Changes
                            aria-label={translate("Decline")}
                        >
                            {translate("Decline")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withHistory(withToaster(OrderApprovalDetailsActionButtons)));
